import { IdentityType, ScopeType } from 'src/common/types';
import DataStore, { Query, QueryResult } from 'src/core/stores/data-store';
import FormStore from 'src/core/stores/form-store';
import { CommandResult } from '../core/stores/types';

export interface RoleAssignmentSummary {
  id: string;
  roleId: string;
  role: string;
  scopeId: string;
  scopeTitle: string;
  scopeType: ScopeType;
  identityId: string; // UserId or GroupId
  identityTitle: string;
  identityType: IdentityType;
  expirationDate?: Date;
  creationDate: Date;
  country: string;
  createdByName: string;
  organizationName: string;
}

export interface CreateRoleAssignmentItem {
  identityId: string;
  identityType: IdentityType;
  roles: RoleWithExpirationDate[];
  scopeId: string;
}

export interface RoleWithExpirationDate {
  role: string;
  roleId: string;
  expirationDate: Date | undefined;
}

export interface RoleAssignmentAttributes {
  expirationDate: Date | undefined;
}

export interface UpdateExpirationRequest {
  scopeId: string;
  newExpirationDate: Date;
}

export class RoleAssignmentSummaryStore extends DataStore<RoleAssignmentSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/role-assignments`, []);
  }

  public async deleteLicenseAssignment(id: string, params?: any) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.delete<any, CommandResult<RoleAssignmentSummary>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/delete-license-role-assignment`,
        params
      );
      return response.data;
    });
  }

  public async loadLicenseAssignments(query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<RoleAssignmentSummary>>(
        `${this.baseUrl}/get-license-role-assignment/?${DataStore.buildUrl(query as Query)}`
      );
      this._state.set((s) => {
        s.items = response.data.items || [];
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }

  public async loadStructmasterRoleAssignments(query: Partial<Query>, ids?: string[]) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<RoleAssignmentSummary>>(
        `${this.baseUrl}/get-structmaster-assignments?${DataStore.buildUrl(query as Query)}`
      );
      this._state.set((s) => {
        s.items = response.data.items || [];
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }

  public async getRoles(query: Partial<Query>) {
    return (
      (await this.handleCallAsync(async () => {
        var response = await this.httpService.get<QueryResult<RoleAssignmentSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query as Query)}`);
        return response.data.items;
      })) ?? []
    );
  }

  public async updateExpiration(id: string, item: UpdateExpirationRequest) {
    return await this.handleCallAsync(async () => {
      const scopeId = item.scopeId;
      var result = await this.httpService.put(`${this.baseUrl}/update-expiration/${scopeId}`, [{ id, newExpirationDate: item.newExpirationDate }]);
      return result.data;
    });
  }

  public async updateExpirationInBulk(payload: { assignmentId: string; scopeId: string; newExpirationDate: Date }[]) {
    return await this.handleCallAsync(async () => {
      const scopeId = payload.firstOrDefault()?.scopeId;
      var result = await this.httpService.put(
        `${this.baseUrl}/update-expiration/${scopeId}`,
        payload.map((v) => ({ newExpirationDate: v.newExpirationDate, id: v.assignmentId }))
      );
      return result.data as { isSuccess: boolean; error?: string };
    });
  }
}

export class RoleAssignmentItemStore extends FormStore<RoleAssignmentSummary, CreateRoleAssignmentItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/role-assignments`);
  }

  public async createLicenseRoleAssignment(item: CreateRoleAssignmentItem) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<CreateRoleAssignmentItem, CommandResult<RoleAssignmentSummary>>(
        `${this.baseUrl}/add-license-role-assignment`,
        item
      );
      return result.data;
    });
  }

  public async createStructMasterRoleAssignment(item: CreateRoleAssignmentItem) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<CreateRoleAssignmentItem, CommandResult<CreateRoleAssignmentItem>>(
        `${this.baseUrl}/create-structmaster-assignments`,
        item
      );
      return result.data;
    });
  }
}
